/**
 * Parse a date string at noon UTC to prevent timezone shifts showing the wrong calendar day.
 * Backend timestamps at midnight UTC display as the previous day in US timezones.
 */
export function parseEventDate(dateString: string): Date {
  return new Date(String(dateString).replace(/T\d{2}:\d{2}:\d{2}/, 'T12:00:00'));
}

/**
 * Format a date string or Date object to a human-readable format
 */
export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const dateObj = typeof date === 'string' ? parseEventDate(date) : date;
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  };
  
  return dateObj.toLocaleDateString(undefined, defaultOptions);
}

/**
 * Format a YYYY-MM-DD date string without timezone conversion
 * Use this for birth dates and other "date-only" fields
 */
export function formatBirthDate(dateString: string | null | undefined): string {
  if (!dateString) return '';
  
  // Handle YYYY-MM-DD format without timezone conversion
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, year = '', month = '', day = ''] = match;
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    return `${monthNames[parseInt(month, 10) - 1]} ${parseInt(day, 10)}, ${year}`;
  }
  
  // Fallback to regular formatDate for other formats
  return formatDate(dateString);
}

/**
 * Calculate age from YYYY-MM-DD birth date string without timezone issues
 */
export function calculateAge(birthDateString: string | null | undefined): number {
  if (!birthDateString) return 0;
  
  // Parse YYYY-MM-DD directly without Date constructor
  const match = birthDateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return 0;

  const [, yearStr = '', monthStr = '', dayStr = ''] = match;
  const birthYear = parseInt(yearStr, 10);
  const birthMonth = parseInt(monthStr, 10); // 1-12
  const birthDay = parseInt(dayStr, 10);
  
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 0-11 to 1-12
  const currentDay = today.getDate();
  
  let age = currentYear - birthYear;
  
  // Adjust if birthday hasn't occurred this year
  if (currentMonth < birthMonth || (currentMonth === birthMonth && currentDay < birthDay)) {
    age--;
  }
  
  return age;
}

/**
 * Format a date to a relative time string (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);
  
  if (diffInSeconds < 60) {
    return 'just now';
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
  }
  
  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks} week${diffInWeeks === 1 ? '' : 's'} ago`;
  }
  
  return formatDate(dateObj);
}

/**
 * Capitalize the first letter of a string
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Truncate a string to a specified length with ellipsis
 */
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}